import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { findLast } from 'lodash';
import { InputBaseModel } from '../../models';
import { InputRenderer, AddLimitButton } from '../../components';
import { LayoutRenderer } from '../../layout_renderers';
import { NodeProps } from '../../componentTypes';
import { InputIds, NodeIds } from '../../misc';
import { Utils } from '../../../custom_shared/misc';
import { useNodeForm } from '../../hooks';
import { ActionParams } from '../../types';
import { GetComponentRowsFunction, ComponentRow } from '../../componentTypes';
import { InputComponentRegistry } from '../../node_inputs';

const addLimitButtonKey = 'add-limit-button';

const InputComponents = InputComponentRegistry[NodeIds.Limits];

const isLimitInput = (input: InputBaseModel) => {
    return input.id.startsWith(InputIds.Limits.LimitType) || input.id.startsWith(InputIds.Limits.LimitValue);
};

const LimitsNodeRenderer: React.FC<NodeProps> = ({ node }: NodeProps) => {
    const { form, formKey, onValuesChange } = useNodeForm(node);

    const getComponentRows: GetComponentRowsFunction = React.useCallback(
        (inputs: InputBaseModel[], actionParams: ActionParams) => {
            const { basicInputs, limitInputs } = inputs.reduce<{
                basicInputs: InputBaseModel[];
                limitInputs: InputBaseModel[];
            }>(
                (acc, input) => {
                    if (isLimitInput(input)) {
                        acc.limitInputs.push(input);
                    } else {
                        acc.basicInputs.push(input);
                    }

                    return acc;
                },
                { basicInputs: [], limitInputs: [] }
            );

            const basicInputRows: ComponentRow[] = Utils.groupArray(basicInputs, 3).map(inputGroup =>
                inputGroup.map(input => ({
                    component: (
                        <InputRenderer
                            key={input.id}
                            input={input}
                            component={InputComponents[input.id as keyof typeof InputComponents]}
                        />
                    )
                }))
            );

            const limitInputRows: ComponentRow[] = Utils.groupArray(limitInputs, 2).map(inputGroup =>
                inputGroup.map(input => ({
                    component: (
                        <InputRenderer
                            key={input.id}
                            input={input}
                            component={
                                input.id.startsWith(InputIds.Limits.LimitType)
                                    ? InputComponents[InputIds.Limits.LimitType]
                                    : InputComponents[InputIds.Limits.LimitValue]
                            }
                        />
                    )
                }))
            );

            const rows = [...basicInputRows, ...limitInputRows];

            const lastRow = findLast(rows, row => row.length < 3);

            const addLimitButton = {
                component: <AddLimitButton key={addLimitButtonKey} actionParams={actionParams} />
            };

            if (lastRow) {
                lastRow.push(addLimitButton);
            } else {
                rows.push([addLimitButton]);
            }

            return rows;
        },
        []
    );

    return (
        <LayoutRenderer
            node={node}
            form={form}
            formKey={formKey}
            onValuesChange={onValuesChange}
            getComponentRows={getComponentRows}
        />
    );
};

export default observer(LimitsNodeRenderer);
